import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import api from '../api/client'
import { NewJobSEO } from '../hooks/useSEO'
import { jobCategories } from '../types'

export default function NewJobPage() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    reward: 100,
    deadline: '',
    mode: 'INSTANT' as 'PROPOSAL' | 'INSTANT',
    category: '',
    tags: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [serverError, setServerError] = useState('')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    setErrors((prev) => ({ ...prev, [name]: '' }))
  }

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (formData.title.length < 5) newErrors.title = 'Title must be at least 5 characters'
    if (formData.description.length < 20) newErrors.description = 'Description must be at least 20 characters'
    if (formData.reward < 1) newErrors.reward = 'Minimum reward is 1 USDC'
    if (formData.reward > 10000) newErrors.reward = 'Maximum reward is 10,000 USDC'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    setIsLoading(true)
    setServerError('')
    try {
      const tags = formData.tags
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0)
        .slice(0, 9)

      await api.post('/jobs', {
        title: formData.title,
        description: formData.description,
        reward: Number(formData.reward),
        deadline: formData.deadline || undefined,
        mode: formData.mode,
        category: formData.category || undefined,
        tags,
      })

      navigate('/my-jobs')
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'Failed to create job')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <NewJobSEO />
      <div style={{ marginBottom: '1.5rem' }}>
        <Link to="/jobs" className="text-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Jobs
        </Link>
      </div>

      <div className="card-static" style={{ maxWidth: 600 }}>
        <h1 style={{ marginBottom: '1.5rem' }}>Post a New Job</h1>

        {serverError && <div className="alert alert-error">{serverError}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="label" htmlFor="title">Title</label>
            <input
              type="text"
              id="title"
              name="title"
              className={`input ${errors.title ? 'input-error' : ''}`}
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Build a REST API"
              disabled={isLoading}
            />
            {errors.title && <p className="form-error">{errors.title}</p>}
          </div>

          <div className="form-group">
            <label className="label" htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              className={`input textarea ${errors.description ? 'input-error' : ''}`}
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe the job requirements, deliverables, and any specific instructions..."
              style={{ minHeight: 150 }}
              disabled={isLoading}
            />
            {errors.description && <p className="form-error">{errors.description}</p>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="label" htmlFor="reward">Reward (USDC)</label>
              <input
                type="number"
                id="reward"
                name="reward"
                className={`input ${errors.reward ? 'input-error' : ''}`}
                value={formData.reward}
                onChange={handleChange}
                min={1}
                max={10000}
                disabled={isLoading}
              />
              {errors.reward && <p className="form-error">{errors.reward}</p>}
              <p className="form-hint">Payment will be made in USDC on Solana</p>
            </div>

            <div className="form-group">
              <label className="label" htmlFor="deadline">Deadline (optional)</label>
              <input
                type="date"
                id="deadline"
                name="deadline"
                className="input"
                value={formData.deadline}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
                disabled={isLoading}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="label" htmlFor="mode">Mode</label>
              <select
                id="mode"
                name="mode"
                className="input select"
                value={formData.mode}
                onChange={handleChange}
                disabled={isLoading}
              >
                <option value="PROPOSAL">Proposal - Review applications first</option>
                <option value="INSTANT">Instant - First come, first served</option>
              </select>
            </div>

            <div className="form-group">
              <label className="label" htmlFor="category">Category</label>
              <select
                id="category"
                name="category"
                className="input select"
                value={formData.category}
                onChange={handleChange}
                disabled={isLoading}
              >
                {jobCategories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="label" htmlFor="tags">Tags (comma separated, max 9)</label>
            <input
              type="text"
              id="tags"
              name="tags"
              className="input"
              value={formData.tags}
              onChange={handleChange}
              placeholder="e.g., nodejs, api, typescript"
              disabled={isLoading}
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? 'Creating...' : 'Create Job'}
          </button>
        </form>
      </div>
    </div>
  )
}
